import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {accessibleObjs, type AccessibleGraphQLObject} from 'graphql-client/InaccessibleInfo';
import type {AaSequenceAminoAcidsQuery} from 'model-hub/__generated/AaSequenceAminoAcidsQuery';
import EntityInputChip from 'model-hub/lib/EntityInputChip';
import FileInputChip from 'model-hub/lib/FileInputChip';
import InputChip from 'model-hub/lib/InputChip';
import StructureTextInputChip from 'model-hub/lib/StructureTextInputChip';
import {PREDICTION_JOB_MOLECULE_SMILES_QUERY, AA_SEQUENCE_AMINO_ACIDS_QUERY} from 'model-hub/ModelHubQueries';
import {getComponentLetter} from 'model-hub/new-prediction/newPredictionState';
import {
  getInlineStructureTarget,
  isAaSequenceIdInputComponent,
  isAminoAcidsInputComponent,
  isFileIdInputComponent,
  isMoleculeIdInputComponent,
  isProteinIdInputComponent,
  isSmilesInputComponent,
  resolvePredictionInputComponents,
  type InlineStructureTarget,
  type PredictionInputComponent,
} from 'model-hub/scientific-models/input-forms/BulkPredictionUtils';
import type {ScientificModelNode} from 'model-hub/scientific-models/ScientificModelMetadata';

import type {InaccessibleInfoGraphQL} from '../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import type {PredictionJobComponent} from './PredictionJobTypes';

type PredictionJobMoleculeNode = AccessibleGraphQLObject & {
  __typename: 'MoleculeNode';
  currentVersion: {
    chemicalStructure: {
      canonicalSmiles: string;
    };
  };
};

interface PredictionJobMoleculeSmilesQuery {
  molecules: Array<PredictionJobMoleculeNode | InaccessibleInfoGraphQL>;
}

export type PreviewSequenceContentKind = 'amino_acids' | 'smiles' | 'file' | 'structure';

/**
 * A single row of a prediction's input table: a named or positionally labeled raw sequence/SMILES, a
 * registry entity whose sequence was resolved via GraphQL, or a target structure given as a Benchling File
 * or as structure text. Structure inputs have no sequence.
 */
export interface PreviewSequenceDisplayItem {
  key: string;
  entityId: string | null;
  /** Component name or positional Chain/Ligand fallback for raw inputs; null for registry entities. */
  rawLabel: string | null;
  contentKind: PreviewSequenceContentKind;
  /** The sequence or SMILES to show; null for inputs that have none, such as files. */
  text: string | null;
  /** For a file input, the version the prediction ran on, when recorded. */
  fileVersionId?: string;
  /** For a target structure given as text, the structure to preview. */
  structure?: InlineStructureTarget;
}

function getCouldNotLoadContentMessage(contentKind: PreviewSequenceContentKind): string {
  if (contentKind === 'smiles') {
    if (!BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE) {
      return 'Molecule chemistry is not enabled on this tenant.';
    }
    return 'Could not load molecule SMILES.';
  }
  return 'Could not load protein sequence.';
}

function previewItemFromComponent(
  component: PredictionInputComponent,
  index: number,
  positionalLabel: string,
  proteinAminoAcidsById: ReadonlyMap<string, string>,
  moleculeSmilesById: ReadonlyMap<string, string>
): PreviewSequenceDisplayItem {
  if (isAminoAcidsInputComponent(component)) {
    return {
      key: `${index}-aminoAcids`,
      entityId: null,
      rawLabel: component.name ?? positionalLabel,
      contentKind: 'amino_acids',
      text: component.value,
    };
  }
  if (isSmilesInputComponent(component)) {
    return {
      key: `${index}-smiles`,
      entityId: null,
      rawLabel: component.name ?? positionalLabel,
      contentKind: 'smiles',
      text: component.value,
    };
  }
  if (isAaSequenceIdInputComponent(component) || isProteinIdInputComponent(component)) {
    const entityId = component.id;
    const aminoAcids = proteinAminoAcidsById.get(entityId);
    return {
      key: `${index}-${component._type}`,
      entityId,
      rawLabel: null,
      contentKind: 'amino_acids',
      text: aminoAcids ?? getCouldNotLoadContentMessage('amino_acids'),
    };
  }
  if (isFileIdInputComponent(component)) {
    return {
      key: `${index}-file`,
      entityId: component.id,
      rawLabel: null,
      contentKind: 'file',
      text: null,
      ...(component.versionId != null ? {fileVersionId: component.versionId} : {}),
    };
  }
  if (isMoleculeIdInputComponent(component)) {
    const smiles = moleculeSmilesById.get(component.id);
    return {
      key: `${index}-molecule`,
      entityId: component.id,
      rawLabel: null,
      contentKind: 'smiles',
      text: smiles ?? getCouldNotLoadContentMessage('smiles'),
    };
  }
  return {
    key: `${index}-unknown`,
    entityId: null,
    rawLabel: 'Component',
    contentKind: 'amino_acids',
    text: '—',
  };
}

/**
 * Resolves a prediction's inputs into display rows: raw components render their inline sequence/SMILES,
 * while registry-entity components (proteins, molecules) are hydrated via GraphQL. Entity IDs are
 * de-duplicated before fetching.
 */
export async function resolvePreviewSequenceItems(
  jobComponents: ReadonlyArray<PredictionJobComponent>,
  model: ScientificModelNode,
  configJson?: Record<string, unknown> | null
): Promise<Array<PreviewSequenceDisplayItem>> {
  const components = resolvePredictionInputComponents(jobComponents, configJson, model);

  const proteinIds = Array.from(
    new Set(
      components.flatMap((component) =>
        isAaSequenceIdInputComponent(component) || isProteinIdInputComponent(component) ? [component.id] : []
      )
    )
  );
  const moleculeIds = Array.from(
    new Set(components.flatMap((component) => (isMoleculeIdInputComponent(component) ? [component.id] : [])))
  );

  const proteinAminoAcidsById = new Map<string, string>();
  if (proteinIds.length > 0) {
    const proteinResult = await doQuerySafe<AaSequenceAminoAcidsQuery>(
      {query: AA_SEQUENCE_AMINO_ACIDS_QUERY},
      {variables: {ids: proteinIds}}
    );
    for (const protein of accessibleObjs(proteinResult.aaSequences)) {
      proteinAminoAcidsById.set(protein.id, protein.experimentalCurrentVersion?.aminoAcids ?? '');
    }
  }

  const moleculeSmilesById = new Map<string, string>();
  if (moleculeIds.length > 0 && BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE) {
    const moleculeResult = await doQuerySafe<PredictionJobMoleculeSmilesQuery>(
      {query: PREDICTION_JOB_MOLECULE_SMILES_QUERY},
      {variables: {moleculeIds}}
    );
    for (const molecule of accessibleObjs<PredictionJobMoleculeNode>(moleculeResult.molecules)) {
      moleculeSmilesById.set(molecule.id, molecule.currentVersion.chemicalStructure.canonicalSmiles);
    }
  }

  let aminoAcidCount = 0;
  let ligandCount = 0;

  const items = components.map((component, index) => {
    const isLigand = isSmilesInputComponent(component) || isMoleculeIdInputComponent(component);
    let positionalLabel = '';
    if (isLigand) {
      ligandCount += 1;
      positionalLabel = `Ligand ${getComponentLetter(ligandCount)}`;
    } else if (!isFileIdInputComponent(component)) {
      aminoAcidCount += 1;
      positionalLabel = `Chain ${getComponentLetter(aminoAcidCount)}`;
    }
    return previewItemFromComponent(
      component,
      index,
      positionalLabel,
      proteinAminoAcidsById,
      moleculeSmilesById
    );
  });

  const structure = getInlineStructureTarget(configJson);
  if (structure != null) {
    items.push({
      key: 'structure',
      entityId: null,
      rawLabel: null,
      contentKind: 'structure',
      text: null,
      structure,
    });
  }
  return items;
}

/** Whether any row has a sequence or SMILES, so tables can omit the column when none do. */
export function hasPreviewSequenceText(items: ReadonlyArray<PreviewSequenceDisplayItem>): boolean {
  return items.some((item) => item.text != null);
}

/** The left "Component" cell of an input row: a clickable structure or entity chip, or a raw input chip. */
export function PreviewSequenceNameCell({item}: {item: PreviewSequenceDisplayItem}): JSX.Element {
  if (item.structure != null) {
    return <StructureTextInputChip structure={item.structure} />;
  }
  if (item.contentKind === 'file' && item.entityId != null) {
    return <FileInputChip fileId={item.entityId} fileVersionId={item.fileVersionId} />;
  }
  if (item.entityId != null) {
    return <EntityInputChip entityId={item.entityId} />;
  }
  return (
    <InputChip
      label={item.rawLabel ?? 'Component'}
      type={item.contentKind === 'smiles' ? 'smiles' : 'aminoAcids'}
      value={item.text ?? undefined}
    />
  );
}
